/**
 * IndexedDB utilities for Noor authentication and profile data
 */

const DB_NAME = 'NoorDB';
const DB_VERSION = 2;

/**
 * Open IndexedDB connection
 */
export function openNoorDB() {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);
        
        request.onupgradeneeded = (event) => {
            const db = event.target.result;
            // Create auth store if it doesn't exist
            if (!db.objectStoreNames.contains('auth')) {
                db.createObjectStore('auth', { keyPath: 'id' });
            }
            // Create profile store if it doesn't exist
            if (!db.objectStoreNames.contains('profile')) {
                db.createObjectStore('profile', { keyPath: 'id' });
            }
        };
        
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
}

/**
 * Save authentication data to IndexedDB
 */
export async function saveAuthData(authData) {
    const db = await openNoorDB();
    return new Promise((resolve, reject) => {
        const transaction = db.transaction('auth', 'readwrite');
        const store = transaction.objectStore('auth');

        store.put({
            id: 1,
            accessToken: authData.access_token,
            refreshToken: authData.refresh_token,
            user: authData.user,
            userRole: authData.user?.user_role || [],
            username: authData.user?.username || '',
            timestamp: Date.now()
        });

        transaction.oncomplete = () => {
            console.log('JWT auth data saved to IndexedDB');
            resolve(true);
        };

        transaction.onerror = (err) => {
            console.error('Failed to save auth data:', err);
            reject(err);
        };
    });
}

/**
 * Get authentication data from IndexedDB
 */
export async function getAuthData() {
    const db = await openNoorDB();
    return new Promise((resolve, reject) => {
        const transaction = db.transaction(['auth'], 'readonly');
        const store = transaction.objectStore('auth');
        const request = store.get(1);
        
        request.onsuccess = () => {
            const authData = request.result || {};
            resolve(authData);
        };
        request.onerror = () => reject(request.error);
    });
}

/**
 * Save profile data to IndexedDB
 */
export async function saveProfileData(profileData) {
    const db = await openNoorDB();
    return new Promise((resolve, reject) => {
        const transaction = db.transaction('profile', 'readwrite');
        const store = transaction.objectStore('profile');

        store.put({
            id: 1,
            subjects: profileData.subjects || [],
            app: profileData.app || {},
            published_media: profileData.published_media || [],
            rally_type: profileData.rally_type || [],
            rally_location: profileData.rally_location || [],
            notifs: profileData.notifs || [],
            user: profileData.user || {},
            timestamp: Date.now()
        });

        transaction.oncomplete = () => {
            console.log('Profile data saved to IndexedDB');
            resolve(true);
        };

        transaction.onerror = (err) => {
            console.error('Failed to save profile data:', err);
            reject(err);
        };
    });
}

/**
 * Get profile data from IndexedDB
 */
export async function getProfileData() {
    const db = await openNoorDB();
    return new Promise((resolve, reject) => {
        const transaction = db.transaction(['profile'], 'readonly');
        const store = transaction.objectStore('profile');
        const request = store.get(1);
        
        request.onsuccess = () => resolve(request.result || {});
        request.onerror = () => reject(request.error);
    });
}

/**
 * Clear all Noor data from IndexedDB
 */
export function clearNoorDB() {
    return new Promise((resolve) => {
        indexedDB.deleteDatabase(DB_NAME)
            .then(() => resolve(true))
            .catch(() => resolve(false));
    });
}
